import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { IconDispatcher } from "shared/ui-basics";
import { GenericButton } from "shared/ui-basics/buttons";
import styled, { css } from "styled-components/macro";
import { useModal } from "use-modal-hook";
import { CustomColorSelectModal } from "./CustomColorSelectModal";

interface ColorSelectModalProps {
	isOpen: boolean;
	onClose: () => void;
	selectedColor: string;
	action: (params: { selectedColor: string }) => void;
	colorsPanel: string[]; // Array of colors to display
	beforeClose?: () => void; // Optional function to run before closing the modal
	colorSize: string; // Size of the color swatches
	isRound?: boolean; // Optional, whether the color swatches are round
	isShowColorPicker?: boolean; // Optional, whether to show the custom color picker
}

export const ColorSelectModal: React.FC<ColorSelectModalProps> = ({ isOpen, onClose, selectedColor, action, colorsPanel, beforeClose, colorSize, isRound = false, isShowColorPicker = false }) => {
	const { t } = useTranslation();
	const [currentColor, setCurrentColor] = useState(selectedColor || "#FFFFFF");

	const [showCustomColorModal] = useModal(CustomColorSelectModal, {
		selectedColor,
		action: ({ selectedColor }: { selectedColor: string }) => {
			action({ selectedColor });
		},
	});

	const openCustomColorSelectModal = () => {
		onClose();
		showCustomColorModal();
	};

	const confirm = () => {
		onClose();
		action({ selectedColor: currentColor });
	};

	const handleReset = () => {
		if (beforeClose) beforeClose();
		onClose();
		action({ selectedColor: "#FFFFFF" });
	};

	return (
		<ModalContainer fade={false} isOpen={isOpen} toggle={onClose} size="sm" centered={true}>
			<ModalHeader toggle={onClose}>{t("SELECT_COLOR")}</ModalHeader>
			<Body>
				<ColorContainer>
					{colorsPanel.map((color) => (
						<Color key={color} color={color} onClick={() => setCurrentColor(color)} size={colorSize} isRound={isRound} showBorder={color === "#FFFFFF"}>
							<Check selected={currentColor?.toLowerCase() === color?.toLowerCase()}>{IconDispatcher("CHECK")}</Check>
						</Color>
					))}
					{isShowColorPicker && <Color color="conic-gradient(#f87171, #fde047, #4ade80, #3b82f6, #c084fc)" onClick={openCustomColorSelectModal} size={colorSize} isRound={true} />}
				</ColorContainer>
			</Body>
			<Footer>
				<GenericButton variant="dark-outline" className="text-capitalized" onClick={handleReset}>
					{t("RESET_COLOR")}
				</GenericButton>
				<GenericButton variant="dark" className="text-capitalized" onClick={confirm}>
					{t("ASPX_12")}
				</GenericButton>
			</Footer>
		</ModalContainer>
	);
};

const ModalContainer = styled(Modal)`
	max-width: 500px;
`;
const Body = styled(ModalBody)`
	padding: 32px;
`;

const ColorContainer = styled.div`
	width: 432px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 8px;
`;

const Color = styled.div<{ isRound: boolean; showBorder: boolean; color: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${({ size }) => size}px;
	height: ${({ size }) => size}px;
	flex-shrink: 0;
	border-radius: 4px;
	background: ${({ color }) => color};
	:hover {
		cursor: pointer;
	}
	${({ isRound }) =>
		isRound &&
		css`
			border-radius: 50%;
		`}
	${({ showBorder }) =>
		showBorder &&
		css`
			border: 1px solid #333;
		`}
`;

const Check = styled.div<{ selected: boolean }>`
	visibility: hidden;
	${({ selected }) =>
		selected &&
		css`
			visibility: visible;
		`}
	path {
		fill: "#333";
	}
`;

const Footer = styled(ModalFooter)`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
